/**
 * Main application component that handles routing and authentication context.
 * Sets up the application's routing structure and provides authentication state
 * to all child components through AuthProvider.
 */

import React from 'react';
import { 
  Route, 
  Navigate,
  createRoutesFromElements,
  createBrowserRouter,
  RouterProvider,
  Outlet
} from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './i18n/config';
import { HeaderProvider } from './context/HeaderContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import Songs from './pages/Songs';
import People from './pages/People';
import Settings from './pages/Settings';
import Metronome from './pages/Metronome';
import Tuner from './pages/Tuner';
import CapoHelper from './pages/CapoHelper';
import LandingPage from './pages/LandingPage';
import BlockoutDates from './pages/BlockoutDates';
import Frequency from './pages/Frequency';
import SongSuggestions from './pages/SongSuggestions';
import Notifications from './pages/Notifications';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import DashboardLayout from './components/layout/DashboardLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProRoute from './components/common/ProRoute';
import SuperAdminRoute from './components/auth/SuperAdminRoute';
import SuperAdminLayout from './components/layout/SuperAdminLayout';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageUsers from './pages/admin/ManageUsers';
import ManageChurches from './pages/admin/ManageChurches';
import ManageSongs from './pages/admin/ManageSongs';
import SupportTickets from './pages/admin/SupportTickets';
import ManageSubscriptions from './pages/admin/ManageSubscriptions';
import GlobalPushNotifications from './pages/admin/GlobalPushNotifications';
import AuditLogs from './pages/admin/AuditLogs';
import SystemSettings from './pages/admin/SystemSettings';

import './styles/globalStyles.css';

// Root layout that includes the auth provider
const RootLayout = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <HeaderProvider>
          <CssBaseline />
          <Outlet />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </HeaderProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

// Protected Dashboard layout
const ProtectedDashboardLayout = () => (
  <ProtectedRoute>
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  </ProtectedRoute>
);

// Protected Super Admin layout
const ProtectedSuperAdminLayout = () => (
  <SuperAdminRoute>
    <SuperAdminLayout>
      <Outlet />
    </SuperAdminLayout>
  </SuperAdminRoute>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/app" element={<ProtectedDashboardLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="songs" element={<Songs />} />
        <Route path="songs/:id/*" element={<Songs />} />
        <Route path="people" element={<People />} />
        <Route path="services" element={<Services />} />
        <Route path="services/:id/*" element={<Services />} />
        <Route path="frequency" element={<Frequency />} />
        <Route path="blockout-dates" element={<BlockoutDates />} />
        <Route path="settings" element={<Settings />} />
        <Route path="metronome" element={<ProRoute><Metronome /></ProRoute>} />
        <Route path="tuner" element={<ProRoute><Tuner /></ProRoute>} />
        <Route path="capo-helper" element={<ProRoute><CapoHelper /></ProRoute>} />
        <Route path="song-suggestions" element={<SongSuggestions />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>
      
      {/* Super Admin Routes */}
      <Route path="/admin" element={<ProtectedSuperAdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<ManageUsers />} />
        <Route path="churches" element={<ManageChurches />} />
        <Route path="subscriptions" element={<ManageSubscriptions />} />
        <Route path="notifications" element={<GlobalPushNotifications />} />
        <Route path="songs" element={<ManageSongs />} />
        <Route path="support" element={<SupportTickets />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="settings" element={<SystemSettings />} />
      </Route>

      {/* Redirect all unknown paths to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true
    }
  }
);

function App() {
  return (
    <RouterProvider router={router} />
  );
}

export default App;
